import { toast } from 'react-toastify';

/**
 * Global date-input guard.
 *
 * Problem: native <input type="date"> (and MUI <TextField type="date">, which
 * renders one) lets a user type an absurd year like 0206 (05-06-0206). Nobody
 * books a hotel in year 206, so we reject such values app-wide instead of
 * adding min/max + validation to all 80+ date fields individually.
 *
 * What it does, once installed:
 *   1. Sets a sensible min/max (1900-01-01 … 2100-12-31) on every date input —
 *      this bounds the native calendar and flags out-of-range typed values.
 *   2. Watches for date inputs added later (dialogs, wizard steps, tables) via
 *      a MutationObserver and bounds those too.
 *   3. On change, if a committed date's year falls outside 1900–2100 (or the
 *      native control reports the value as out of range), it clears the field
 *      so the user must pick a valid date, and shows a short warning.
 *
 * The range is deliberately wide so it never blocks legitimate dates — past
 * dates (DOB, reports) and future dates (bookings) all fall inside it; only
 * clearly malformed years (e.g. 206, 20206) are rejected.
 */

const MIN_DATE = '1900-01-01';
const MAX_DATE = '2100-12-31';
const MIN_YEAR = 1900;
const MAX_YEAR = 2100;

// Update a controlled <input>'s value so React's onChange fires. Setting
// el.value directly is ignored by React; we must call the native value setter
// and dispatch an input event. Works with React 16–19.
const setReactInputValue = (el, value) => {
  const setter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value',
  )?.set;
  if (setter) setter.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event('input', { bubbles: true }));
  el.dispatchEvent(new Event('change', { bubbles: true }));
};

const isDateInput = (el) =>
  el && el.tagName === 'INPUT' && el.type === 'date';

// Apply the min/max bounds, but never override bounds a field already sets.
const applyBounds = (el) => {
  if (!isDateInput(el)) return;
  if (!el.min) el.min = MIN_DATE;
  if (!el.max) el.max = MAX_DATE;
};

// Year from an ISO date value like "0206-06-05" → 206.
const yearOf = (value) => {
  const m = /^(\d{1,6})-\d{1,2}-\d{1,2}$/.exec(value || '');
  return m ? parseInt(m[1], 10) : null;
};

const validate = (el) => {
  if (!isDateInput(el) || !el.value) return;
  const year = yearOf(el.value);
  const outOfRange =
    el.validity?.rangeUnderflow ||
    el.validity?.rangeOverflow ||
    el.validity?.badInput ||
    (year !== null && (year < MIN_YEAR || year > MAX_YEAR));
  if (outOfRange) {
    setReactInputValue(el, ''); // clear → React state resets, user re-picks
    toast.warn('Please enter a valid date.');
  }
};

let installed = false;

export const installDateGuard = () => {
  if (installed || typeof document === 'undefined') return () => {};
  installed = true;

  // Bound any date inputs already on the page.
  document.querySelectorAll('input[type="date"]').forEach(applyBounds);

  // Bound date inputs added later (dialogs, wizard steps, etc.).
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return; // element nodes only
        if (isDateInput(node)) applyBounds(node);
        node.querySelectorAll?.('input[type="date"]').forEach(applyBounds);
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Reject out-of-range years ONLY on blur (when the user finishes and leaves
  // the field), never while typing. Validating on input/change would fire
  // mid-entry — e.g. typing the first year digit "2" momentarily reads as year
  // 0002 and would wrongly clear the field. Blur (capture phase, via event
  // delegation) covers every date input, including ones added later.
  const onBlur = (e) => {
    if (isDateInput(e.target)) validate(e.target);
  };
  document.addEventListener('blur', onBlur, true);

  // eslint-disable-next-line no-console
  console.info('[dateGuard] installed — date year range', MIN_YEAR, '–', MAX_YEAR);

  // Cleanup (not used in practice — installed once for the app's lifetime).
  return () => {
    observer.disconnect();
    document.removeEventListener('blur', onBlur, true);
    installed = false;
  };
};
